import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { examinerRequest } from '../../utils/examinerApi';

export const useExaminer = () => {
  const { currentUser, logout } = useApp();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState([]);
  const active = useRef(true);
  const pending = useRef(false);
  const cooldown = useRef(0);
  const onMissing = useRef(null);
  const controller = useRef(new AbortController());
  useEffect(() => {
    active.current = true;
    if (controller.current.signal.aborted) controller.current = new AbortController();
    const requestController = controller.current;
    return () => { active.current = false; requestController.abort(); };
  }, []);
  const request = useCallback(async (path, options = {}) => {
    if (Date.now() < cooldown.current) throw new Error(`Request limit reached. Retry manually after ${new Date(cooldown.current).toLocaleTimeString()}.`);
    try {
      return await examinerRequest(currentUser?.sessionId, path, { ...options, signal: controller.current.signal });
    } catch (failure) {
      if (failure.status === 401) logout();
      if (failure.status === 429 && failure.retryAt) cooldown.current = failure.retryAt;
      if (failure.status === 403 && active.current) setForbidden(previous => [...previous, path]);
      throw failure;
    }
  }, [currentUser?.sessionId, logout]);
  const run = useCallback(async task => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    try { await task(); }
    catch (failure) {
      if (active.current && failure.name !== 'AbortError') {
        setError(failure);
        if (failure.status === 404 && onMissing.current) {
          try { await onMissing.current(failure); }
          catch (refreshError) { if (active.current) setError(refreshError); }
        }
      }
    }
    finally { pending.current = false; if (active.current) setBusy(false); }
  }, []);
  return { request, run, error, setError, busy, forbidden, active, onMissing };
};

export const ExaminerError = ({ error }) => {
  const preparing = ['queued', 'processing', 'running'].includes(error?.readiness?.status);
  return error && <div role={preparing ? 'status' : 'alert'} className="examiner-error">
  {error.status === 503 && <strong>Setup/unavailable: </strong>}
  {error.status === 409 && <strong>{preparing ? 'Checkride preparation in progress: '
    : error.readiness?.status === 'needs_input' ? 'Setup needs attention: ' : 'State/review conflict: '}</strong>}
  {error.message}
  <ExaminerReadiness readiness={error.readiness} />
  {error.status === 401 && ' Please sign in again.'}
  {error.status === 403 && ' This action is not permitted for your account.'}
  {error.status === 429 && (error.code === 'examiner_daily_limit'
    ? ' No automatic retries. Resume an existing examination, or finish it to release unused reserved time. New examinations use the remaining allowance, provided at least one whole minute and one daily session remain.'
    : ` No automatic retries. Retry manually after ${new Date(error.retryAt).toLocaleTimeString()}.`)}
</div>;
};

export const ExaminerReadiness = ({ readiness, aircraft = [] }) => readiness && <section aria-label="Examiner readiness">
  <p role="status">Examiner readiness: <strong>{readiness.status}</strong></p>
  <ul>{(readiness.blockers || []).filter((item, index, items) => items.findIndex(other => other.code === item.code && other.message === item.message && String(other.aircraftId ?? '') === String(item.aircraftId ?? '')) === index).map((item, index) => <li key={index}>
    {item.code}: {item.message}{item.aircraftId ? ` (aircraft ${aircraft.find(plane => Number(plane.id) === Number(item.aircraftId))?.tailNumber || item.aircraftId})` : ''}
  </li>)}</ul>
  {!!readiness.plans?.length && <details><summary>Automatic checkride initialization plans</summary>
    <ul>{readiness.plans.map((plan, index) => <li key={index}>
      {aircraft.find(plane => Number(plane.id) === Number(plan.aircraftId))?.tailNumber || `Aircraft ${plan.aircraftId}`} / {plan.certificate}: {plan.status}
      {plan.jobId && ` — job ${plan.jobId}`}{plan.curriculumId && ` — curriculum ${plan.curriculumId}`}
    </li>)}</ul>
  </details>}
</section>;

export const SourceCitation = ({ source }) => {
  const { currentUser } = useApp();
  const { request, run, busy, error } = useExaminer();
  const url = source.sourceUrl || source.source_url;
  const documentId = /^urn:realair:examiner-document:([0-9a-f-]{36})$/i.exec(url || '')?.[1];
  const libraryId = /^urn:realair:examiner-library:([0-9a-f-]{36})$/i.exec(url || '')?.[1];
  const staff = ['admin', 'superuser', 'instructor'].includes(currentUser?.user?.orgRole);
  const safe = typeof url === 'string' && /^https:\/\//i.test(url);
  return <div className="examiner-source">
    <strong>{source.title || source.id}</strong> — {source.id}
    <div>{documentId || libraryId ? staff && <button disabled={busy} onClick={() => run(async () => {
      const blob = await request(libraryId ? `/documents/${libraryId}/original` : `/aircraft-documents/${documentId}`, { binary: true });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl; link.download = `examiner-document-${libraryId || documentId}.${blob.type === 'application/pdf' ? 'pdf' : 'txt'}`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    })}>{libraryId ? 'Download private library original' : 'Download private aircraft original'}</button> : safe ? <a href={url} target="_blank" rel="noopener noreferrer">{url}</a> : url}</div>
    <ExaminerError error={error} />
    <div>Revision: {source.revision}; locator: {source.locator}; expires: {source.validUntil || source.valid_until}</div>
    {source.applicability && <p>Aircraft applicability: {source.applicability}</p>}
    {source.content && <pre>{source.content}</pre>}
  </div>;
};

export const AircraftPicker = ({ aircraft, value, onChange, disabled = false }) => <label>Aircraft
  <select required disabled={disabled} value={value} onChange={event => onChange(event.target.value)}>
    <option value="">Select aircraft</option>
    {(aircraft || []).map(item => <option key={item.id} value={item.id}>{item.make} {item.model} — {item.tailNumber || item.tail_number || item.registration}</option>)}
  </select>
</label>;
